import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import Drawer from "../../../../components/ui/Drawer";
import { useScriptMode } from "../../../../contexts/ScriptModeContext";
import { useToast } from "../../../../components/ui/Toast";
import {
  useGetMemberDaysQuery,
  useUpsertMemberLogEntryMutation,
} from "../../../../api/dailyLogApi";
import {
  changedRows,
  completeAll,
  dayProgress,
  lastDays,
  memberDayRows,
  resetAll,
  todayString,
  toPayload,
} from "./memberDayHelpers";
import ConnectionState from "../../../../components/ui/ConnectionState";

const STRIP_DAYS = 7;
const DAY_NAMES = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const UNIT_WORDS = { page: "sayfa", line: "satır", minute: "dk", count: "adet", tally: "adet" };
const ACTIVITY_WORDS = { okuma: "Okuma", ezber: "Ezber", yazma: "Yazma", dinleme: "Dinleme", calisma: "Çalışma" };

// Kaydet ve tamamla yeşil; geri al yumuşak kırmızı (mobille aynı tonlar).
const GREEN = "bg-[#2E9E6A] dark:bg-[#34B37A] text-white";
const SOFT_RED =
  "bg-[rgba(192,57,43,0.08)] text-[#C0392B] dark:bg-[rgba(242,165,156,0.14)] dark:text-[#F2A59C]";

const dayOf = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

/**
 * Yöneticinin kardeş adına günlük girişi (Hedef Gir): son 7 günden biri
 * seçilir, kardeşin o günkü grup hedeflerine değer girilir. Kaydet yalnız
 * değişen satırları gönderir; sunucu satırı "Yönetici girdi" diye işaretler.
 */
export default function MemberDayEntryDrawer({ open, onClose, groupId, member }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const today = useMemo(() => todayString(), []);
  const days = useMemo(() => lastDays(today, STRIP_DAYS), [today]);
  const memberId = member?.id ? String(member.id) : null;

  const { data, isFetching, isError, error, refetch } = useGetMemberDaysQuery(
    { groupId, memberId, startDate: days[0], endDate: days[days.length - 1] },
    { skip: !open || !groupId || !memberId, refetchOnMountOrArgChange: true }
  );
  const [upsert, { isLoading: saving }] = useUpsertMemberLogEntryMutation();

  const [selected, setSelected] = useState(today);
  const [draft, setDraft] = useState(null);
  const [droppedChanges, setDroppedChanges] = useState(false);

  // Pencere her açılışta bugünden başlar.
  useEffect(() => {
    if (open) {
      setSelected(today);
      setDraft(null);
      setDroppedChanges(false);
    }
  }, [open, today]);

  const rowsByDay = useMemo(() => {
    const groupGoals = data?.data?.member?.goals || [];
    const byDate = {};
    for (const d of data?.data?.days || []) byDate[d.localDate] = d.dailyLogs || [];
    const map = {};
    for (const day of days) map[day] = memberDayRows(byDate[day] || [], groupGoals, day, today);
    return map;
  }, [data, days, today]);

  const baseRows = rowsByDay[selected] || [];
  const rows = draft ?? baseRows;
  const changed = draft ? changedRows(draft, baseRows) : [];
  const anyValue = rows.some((r) => Number(r.value) > 0);
  const allDone = rows.length > 0 && rows.every((r) => Number(r.value) >= Number(r.goalValue));

  const selectDay = (day) => {
    if (day === selected) return;
    setDroppedChanges(changed.length > 0);
    setDraft(null);
    setSelected(day);
  };

  const setValue = (index, value) => {
    setDroppedChanges(false);
    setDraft((prev) =>
      (prev ?? baseRows).map((row, i) =>
        i === index ? { ...row, value: Math.max(0, value), enteredBy: undefined } : row
      )
    );
  };

  const save = async () => {
    if (!changed.length) return;
    const result = await upsert({
      groupId,
      memberId,
      localDate: selected,
      dailyLogs: changed.map(toPayload),
    });
    if (result.error) {
      toast.error(result.error.message || t("Kayıt kaydedilirken bir hata oluştu"));
      return;
    }
    setDraft(null);
    toast.success(t("Kayıt güncellendi"));
  };

  const dayTone = (day) => {
    const dayRows = rowsByDay[day] || [];
    if (!dayRows.length) return "empty";
    const progress = dayProgress(dayRows);
    if (dayRows.every((r) => Number(r.value) >= Number(r.goalValue))) return "done";
    if (progress > 0) return "partial";
    return day === today ? "empty" : "missed";
  };
  const TONE = {
    done: "bg-terakkiBrandMist text-terakkiPrimaryDark dark:bg-[rgba(72,166,167,0.45)] dark:text-white",
    partial: "bg-terakkiWarning text-white",
    missed: "bg-terakkiError text-white",
    empty:
      "bg-terakkiBrandCloud text-terakkiInk dark:bg-white/10 dark:text-white/80",
  };

  const footer = (
    <button
      type="button"
      onClick={save}
      disabled={!changed.length || saving}
      className={`w-full h-11 rounded-full font-SemiBold flex items-center justify-center gap-2 transition-opacity ${GREEN} ${
        !changed.length || saving ? "opacity-45" : ""
      }`}
    >
      {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : null}
      {t("Kaydet")}
    </button>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="sm"
      title={t("Hedef Gir")}
      subtitle={member ? `${member.name || ""} ${member.surname || ""}`.trim() : ""}
      titleIcon={<EditNoteOutlinedIcon />}
      footer={footer}
    >
      {/* Üstte sabit: gün şeridi ve toplu düğmeler */}
      <div className="sticky top-0 z-10 bg-terakkiBrandCloud dark:bg-[#0f3234] border-b border-terakkiBorder dark:border-white/10 px-4 pt-3 pb-3 flex flex-col gap-3">
        <div className="grid grid-cols-7 gap-1.5">
          {days.map((day) => {
            const active = day === selected;
            return (
              <button
                key={day}
                type="button"
                onClick={() => selectDay(day)}
                className={`h-14 rounded-xl flex flex-col items-center justify-center text-xs transition ${TONE[dayTone(day)]} ${
                  active ? "ring-2 ring-terakkiPrimaryDark dark:ring-white" : ""
                }`}
              >
                <span className="opacity-80">{t(DAY_NAMES[dayOf(day)])}</span>
                <span className="text-base font-SemiBold">{Number(day.slice(8))}</span>
              </button>
            );
          })}
        </div>
        {droppedChanges ? (
          <p className="text-xs text-terakkiWarning font-SemiBold">
            {t("Önceki gündeki değişiklik kaydedilmedi")}
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={!anyValue}
            onClick={() => {
              setDroppedChanges(false);
              setDraft((prev) => resetAll(prev ?? baseRows));
            }}
            className={`h-10 rounded-full text-sm font-SemiBold ${SOFT_RED} ${!anyValue ? "opacity-45" : ""}`}
          >
            {t("Hepsini Geri Al")}
          </button>
          <button
            type="button"
            disabled={!rows.length || allDone}
            onClick={() => {
              setDroppedChanges(false);
              setDraft((prev) => completeAll(prev ?? baseRows));
            }}
            className={`h-10 rounded-full text-sm font-SemiBold ${GREEN} ${
              !rows.length || allDone ? "opacity-45" : ""
            }`}
          >
            {t("Hepsini Tamamla")}
          </button>
        </div>
      </div>

      {isFetching && !data ? (
        <div className="flex justify-center py-10">
          <CircularProgress size={28} />
        </div>
      ) : isError ? (
        <ConnectionState
          error={error}
          onRetry={refetch}
          retrying={isFetching}
          message={t("Günler yüklenemedi")}
          compact
        />
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-terakkiNeutral dark:text-white/70">
          {t("Bu gün için planlı grup hedefi yok")}
        </p>
      ) : (
        <ul>
          {rows.map((row, index) => {
            const done = Number(row.value) >= Number(row.goalValue) && Number(row.goalValue) > 0;
            const pct =
              Number(row.goalValue) > 0
                ? Math.min(100, Math.round((Number(row.value) / Number(row.goalValue)) * 100))
                : 0;
            const unit = UNIT_WORDS[row.unit] || "";
            return (
              <li
                key={`${row.label}-${row.activity || ""}`}
                className="px-4 py-3 border-b border-terakkiBorder dark:border-white/10 flex flex-col gap-2"
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-SemiBold text-terakkiInk dark:text-white">
                      {row.label}
                    </div>
                    <div className="text-xs text-terakkiNeutral dark:text-white/60">
                      {[ACTIVITY_WORDS[row.activity] || "Okuma", `${t("Hedef")} ${row.goalValue} ${unit}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      aria-label={t("goal_decrease")}
                      disabled={Number(row.value) <= 0}
                      onClick={() => setValue(index, Number(row.value) - 1)}
                      className="w-9 h-9 rounded-full bg-terakkiBrandCloud dark:bg-white/10 text-terakkiPrimaryDark dark:text-terakkiPrimaryLight flex items-center justify-center disabled:opacity-40"
                    >
                      <RemoveIcon fontSize="small" />
                    </button>
                    <input
                      type="number"
                      min={0}
                      value={row.value}
                      onChange={(e) => setValue(index, Number(e.target.value) || 0)}
                      className="w-14 h-9 rounded-lg text-center text-sm font-SemiBold border border-terakkiBorder bg-white text-terakkiInk dark:bg-white/10 dark:border-white/15 dark:text-white"
                    />
                    <button
                      type="button"
                      aria-label={t("goal_increase")}
                      onClick={() => setValue(index, Number(row.value) + 1)}
                      className="w-9 h-9 rounded-full bg-terakkiBrandCloud dark:bg-white/10 text-terakkiPrimaryDark dark:text-terakkiPrimaryLight flex items-center justify-center"
                    >
                      <AddIcon fontSize="small" />
                    </button>
                  </div>
                </div>
                <div className="h-1 rounded-full bg-terakkiBrandMist dark:bg-white/10 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${done ? "bg-[#2E9E6A] dark:bg-[#34B37A]" : "bg-terakkiPrimary dark:bg-terakkiPrimaryLight"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {row.enteredBy ? (
                  <div className="text-xs text-terakkiNeutral dark:text-white/60">
                    {t("Yönetici girdi")}
                    {row.enteredBy.name ? ` · ${row.enteredBy.name}` : ""}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}
